import { Sigma } from 'lucide-react';
import { getDeviations, activeWithNumbers } from '../../../utils/elementShape';
import { fmt } from '../measure/numFormat';
import { beitragsAnteile, beitragsLabel } from '../../../utils/contributions';
import WissenHinweis from '../WissenHinweis';

// Beitragsleister — per-element % contribution to the closure's
// total tolerance budget. Replaces the legacy StackBar's absolute
// values per the prototype's reframing (results.jsx:309-456):
// since the chain itself is drawn above in the MassketteDiagram,
// this block carries different information — share of the closure.
//
// Anteile kommen aus utils/contributions.js (gleiche Rechnung wie PDF
// und CSV; Monte Carlo nach Varianz der gewählten Verteilung).

// 7-colour palette, repeats after 7 elements. Match the prototype's
// COLORS array exactly so the colour assignment stays predictable
// when a user has already memorised which slot is which.
const COLORS = ['#1d51ff', '#0aa3a3', '#7c3aed', '#e11d8a', '#16a34a', '#d97706', '#0891b2'];

export default function BeitragsLeister({ elements, mode }) {
  // C5: nur aktive Maße, Beschriftung nach Tabellennummer (nr).
  const active = activeWithNumbers(elements);
  if (!active.length) return null;

  const anteile = beitragsAnteile(active, mode);
  const contribs = active.map((el, i) => {
    const { lower, upper } = getDeviations(el);
    return { row: el, range: Math.abs(upper - lower), share: anteile[i] };
  });
  const methodLabel = beitragsLabel(mode);

  return (
    <div
      data-snippet="beitrag"
      className="bg-surface border border-line"
      style={{ borderRadius: 'var(--radius-md)', padding: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Sigma size={14} className="text-brand" aria-hidden="true" />
        <div className="text-ink-2" style={{ fontSize: 12, fontWeight: 600 }}>Beitragsleister <WissenHinweis id="beitrag" /></div>
        <div className="num text-ink-3" style={{ marginLeft: 'auto', fontSize: 11 }}>
          {methodLabel}
        </div>
      </div>
      <div className="text-ink-3" style={{ fontSize: 10.5, lineHeight: 1.45, marginBottom: 12 }}>
        Anteil jedes Maßes am gesamten Toleranzfeld von M₀.
      </div>

      {/* Stacked horizontal bar — width = % share, colour = palette
          slot. Direction-negative elements render at lower opacity so
          the eye registers them as "minuend" without losing the colour. */}
      <div
        className="bg-surface-2 border border-line"
        style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden' }}
      >
        {contribs.map((c, i) => {
          const sign = c.row.direction === -1 ? -1 : 1;
          const color = COLORS[(c.row.nr - 1) % COLORS.length];
          if (c.share < 0.5) return null;
          return (
            <div
              key={c.row.id}
              title={`${c.row.name || `M${c.row.nr}`} · ${c.share.toFixed(1)} %`}
              style={{
                width: `${c.share}%`,
                background: color,
                opacity: sign < 0 ? 0.65 : 0.95,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 9, fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                borderRight: i < contribs.length - 1 ? '1px solid rgba(255,255,255,0.4)' : 'none',
              }}
            >
              {c.share > 8 ? c.row.nr : ''}
            </div>
          );
        })}
      </div>

      {/* Per-element legend rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
        {contribs.map((c) => {
          const sign = c.row.direction === -1 ? -1 : 1;
          const color = COLORS[(c.row.nr - 1) % COLORS.length];
          return (
            <div
              key={c.row.id}
              className="text-ink-2"
              style={{
                display: 'grid',
                gridTemplateColumns: '14px 18px 1fr 60px 48px',
                gap: 8, alignItems: 'center',
                fontSize: 11, padding: '2px 0',
              }}
            >
              <span
                style={{
                  width: 10, height: 10, borderRadius: 2,
                  background: color, opacity: sign < 0 ? 0.65 : 0.95,
                  display: 'inline-block',
                }}
              />
              <span className="num text-ink-4" style={{ fontSize: 10, fontWeight: 600 }}>
                {String(c.row.nr).padStart(2, '0')}
              </span>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                M{c.row.nr}
                <span className="text-ink-3" style={{ marginLeft: 6 }}>{c.row.name || ''}</span>
              </span>
              <span
                className="num text-ink-2"
                title="Toleranzbreite"
                style={{ fontWeight: 600, textAlign: 'right', fontSize: 10.5 }}
              >
                {fmt(c.range, 3)}
              </span>
              <span className="num text-ink" style={{ fontWeight: 700, textAlign: 'right', fontSize: 11.5 }}>
                {c.share.toFixed(1)} %
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
